import React, { useMemo } from 'react';
import { Building2, Calendar, Clock3, Pencil } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { DocumentSchema, InputNode, ViewNode } from '../../documents/types';
import { stripHtml } from '../../documents/utils/documentViewUtils';

interface CaseDocumentReadViewProps {
    schema: DocumentSchema;
    data: Record<string, any>;
    dataSources?: Record<string, any[]>;
    readOnlyFieldIds?: string[];
    meta?: {
        caseId?: string;
        status?: string;
        createdAt?: string;
    };
    canEdit?: boolean;
    onEdit?: () => void;
}

const isEmptyValue = (value: any) =>
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

const metadataLabelClassName =
    'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

const fieldLabelClassName =
    'text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-foreground';

const repeaterSubtitleClassName =
    'text-[10px] font-medium tracking-[0.01em] text-muted-foreground';

const formatDateValue = (value?: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatDateTimeValue = (value?: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const evaluateCondition = (
    condition: string | undefined,
    rootData: Record<string, any>,
    localContext?: Record<string, any>
): boolean => {
    if (!condition) return false;

    const getValue = (key: string): any => {
        if (key.endsWith('.length')) {
            const baseKey = key.slice(0, -7);
            const baseValue = getValue(baseKey);
            return Array.isArray(baseValue) ? baseValue.length : 0;
        }
        if (localContext && localContext[key] !== undefined) {
            return localContext[key];
        }
        return rootData[key];
    };

    const evaluateExpression = (expression: string): boolean => {
        const trimmed = expression.trim();

        if (trimmed.includes('||')) {
            return trimmed.split('||').some((part) => evaluateExpression(part));
        }
        if (trimmed.includes('&&')) {
            return trimmed
                .split('&&')
                .every((part) => evaluateExpression(part));
        }
        if (trimmed.startsWith('!')) {
            const key = trimmed.slice(1).trim();
            if (
                !key.includes(' ') &&
                !key.includes('=') &&
                !key.includes('>') &&
                !key.includes('<')
            ) {
                return !getValue(key);
            }
        }
        if (trimmed.includes('!==')) {
            const [key, value] = trimmed
                .split('!==')
                .map((part) => part.trim());
            return getValue(key) !== value.replace(/['"]/g, '');
        }
        if (trimmed.includes('===')) {
            const [key, value] = trimmed
                .split('===')
                .map((part) => part.trim());
            return getValue(key) === value.replace(/['"]/g, '');
        }
        if (trimmed.includes('==')) {
            const [key, value] = trimmed.split('==').map((part) => part.trim());
            // eslint-disable-next-line eqeqeq
            return getValue(key) == value.replace(/['"]/g, '');
        }
        if (trimmed.includes('>')) {
            const [key, value] = trimmed.split('>').map((part) => part.trim());
            return Number(getValue(key)) > Number(value);
        }
        if (trimmed.includes('<')) {
            const [key, value] = trimmed.split('<').map((part) => part.trim());
            return Number(getValue(key)) < Number(value);
        }
        return !!getValue(trimmed);
    };

    return evaluateExpression(condition);
};

const resolveOptionLabel = (
    field: InputNode,
    rawValue: any,
    dataSources?: Record<string, any[]>
): string => {
    if (rawValue === undefined || rawValue === null) return '';

    const sourceKey = field.data?.options?.source;
    if (sourceKey && Array.isArray(dataSources?.[sourceKey])) {
        const sourceOptions = dataSources?.[sourceKey] || [];
        const option = sourceOptions.find((candidate: any) => {
            if (typeof candidate === 'string') return candidate === rawValue;
            return (
                candidate?.value === rawValue ||
                candidate?._id === rawValue ||
                candidate?.id === rawValue
            );
        });

        if (option) {
            return typeof option === 'string'
                ? option
                : option.label ||
                      option.name ||
                      option.value ||
                      String(rawValue);
        }
    }

    const fixedOption = field.data?.options?.fixed?.find(
        (option) => option.value === rawValue
    );
    if (fixedOption) return fixedOption.label;

    return String(rawValue);
};

const formatInputValue = (
    field: InputNode,
    value: any,
    dataSources?: Record<string, any[]>
): React.ReactNode => {
    if (isEmptyValue(value)) return null;

    if (field.inputType === 'richtext' && typeof value === 'string') {
        return (
            <span className="whitespace-pre-wrap leading-relaxed">
                {stripHtml(value)}
            </span>
        );
    }

    if (field.inputType === 'date') {
        return formatDateValue(String(value));
    }

    if (field.inputType === 'select' || field.inputType === 'radio') {
        return resolveOptionLabel(field, value, dataSources);
    }

    if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] !== 'object') {
            return value.join(', ');
        }
        return value
            .map((item) =>
                typeof item === 'object'
                    ? Object.values(item)
                          .filter((entry) => !isEmptyValue(entry))
                          .join(' - ')
                    : String(item)
            )
            .join('\n');
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (typeof value === 'string' && /^https?:\/\//.test(value)) {
        return (
            <a
                href={value}
                target="_blank"
                rel="noreferrer"
                className="break-all text-foreground/85 underline underline-offset-2 hover:text-white"
            >
                {value}
            </a>
        );
    }

    return (
        <span className="whitespace-pre-wrap leading-relaxed">
            {String(value)}
        </span>
    );
};

const renderRepeaterItems = (
    field: InputNode,
    items: any[],
    rootData: Record<string, any>,
    dataSources?: Record<string, any[]>
) => {
    if (!items.length) return null;

    const renderedItems = items
        .map((item, itemIndex) => {
            const renderedChildren = (field.fields || [])
                .map((childNode) => {
                    if (childNode.kind !== 'input') return null;
                    if (
                        childNode.hidden &&
                        evaluateCondition(childNode.hidden, rootData, item)
                    ) {
                        return null;
                    }

                    const childValue = item?.[childNode.id];
                    if (isEmptyValue(childValue)) return null;

                    return (
                        <div
                            key={`${field.id}-${itemIndex}-${childNode.id}`}
                            className="min-w-0 flex flex-wrap items-baseline gap-x-1.5 gap-y-1"
                        >
                            <div className={repeaterSubtitleClassName}>
                                {childNode.title}
                            </div>
                            <div className="min-w-0 text-[14px] leading-6">
                                {formatInputValue(
                                    childNode,
                                    childValue,
                                    dataSources
                                )}
                            </div>
                        </div>
                    );
                })
                .filter(Boolean);

            if (!renderedChildren.length) {
                return null;
            }

            return (
                <div
                    key={`${field.id}-${itemIndex}`}
                    className="flex flex-wrap gap-x-4 gap-y-2 py-2.5 first:pt-0 last:pb-0"
                >
                    {renderedChildren}
                </div>
            );
        })
        .filter(Boolean);

    if (!renderedItems.length) {
        return null;
    }

    return (
        <div className="divide-y divide-border">
            {renderedItems}
        </div>
    );
};

const renderViewField = (
    field: ViewNode,
    data: Record<string, any>
): React.ReactNode => {
    if (field.viewType === 'total-summary') {
        const dependency = field.viewProps?.dependency || 'allTasks';
        const sumField = field.viewProps?.sumField || 'minutes';
        const items = Array.isArray(data[dependency]) ? data[dependency] : [];
        if (!items.length) return null;

        const total = items.reduce(
            (sum, item) => sum + Number(item?.[sumField] || 0),
            0
        );
        return (
            <div className="space-y-1">
                <div className="text-[12px] text-muted-foreground">
                    {field.title}
                </div>
                <div className="text-[34px] font-semibold leading-none">
                    {total}{' '}
                    <span className="text-base font-normal text-muted-foreground">
                        min
                    </span>
                </div>
            </div>
        );
    }

    if (field.viewType === 'cpt-codes') {
        const cptCodes = Array.isArray(data.cptCodes) ? data.cptCodes : [];
        if (!cptCodes.length) return null;

        return (
            <div className="space-y-1">
                <div className="text-[12px] text-muted-foreground">
                    {field.title}
                </div>
                <div className="flex flex-wrap gap-2">
                    {cptCodes.map((code, index) => (
                        <Badge key={`${field.id}-${index}`} variant="outline">
                            {code?.label || code?.code || String(code)}
                        </Badge>
                    ))}
                </div>
            </div>
        );
    }

    return null;
};

export const CaseDocumentReadView: React.FC<CaseDocumentReadViewProps> = ({
    schema,
    data,
    dataSources,
    readOnlyFieldIds = [],
    meta,
    canEdit = false,
    onEdit,
}) => {
    const scannerOptions = useMemo(
        () => dataSources?.scanners || [],
        [dataSources?.scanners]
    );

    const scanDate = data?.scanDate || data?.assessmentDate;
    const scannerLabel = useMemo(() => {
        if (!data?.scannerId) return '';
        const scannerOption = scannerOptions.find((scanner: any) => {
            if (typeof scanner === 'string') return scanner === data.scannerId;
            return (
                scanner?.value === data.scannerId ||
                scanner?._id === data.scannerId ||
                scanner?.id === data.scannerId
            );
        });

        if (!scannerOption) return String(data.scannerId);
        return typeof scannerOption === 'string'
            ? scannerOption
            : scannerOption.label ||
                  scannerOption.roomName ||
                  scannerOption.model ||
                  '';
    }, [data?.scannerId, scannerOptions]);

    return (
        <div className="space-y-6">
            {canEdit && onEdit && (
                <div className="flex justify-end">
                    <Button size="sm" onClick={onEdit}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Document
                    </Button>
                </div>
            )}

            <div className="grid gap-3 text-sm md:grid-cols-3">
                <Card>
                    <CardContent className="space-y-1 p-4">
                        <div
                            className={`${metadataLabelClassName} flex items-center gap-1`}
                        >
                            <Calendar className="h-4 w-4" />
                            Scan Date
                        </div>
                        <div className="text-[15px] leading-6">
                            {formatDateValue(scanDate) || 'Not set'}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="space-y-1 p-4">
                        <div
                            className={`${metadataLabelClassName} flex items-center gap-1`}
                        >
                            <Clock3 className="h-4 w-4" />
                            Created
                        </div>
                        <div className="text-[15px] leading-6">
                            {formatDateTimeValue(meta?.createdAt) ||
                                'Not available'}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="space-y-1 p-4">
                        <div
                            className={`${metadataLabelClassName} flex items-center gap-1`}
                        >
                            <Building2 className="h-4 w-4" />
                            Facility
                        </div>
                        <div className="text-[15px] leading-6">
                            {scannerLabel || 'Not set'}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="space-y-6">
                {schema.sections.map((section) => {
                    if (
                        section.sectionType === 'actions' ||
                        (section.hidden &&
                            evaluateCondition(section.hidden, data))
                    ) {
                        return null;
                    }

                    const renderedFields = section.fields
                        .map((node) => {
                            if (
                                node.hidden &&
                                evaluateCondition(node.hidden, data)
                            ) {
                                return null;
                            }
                            if (node.kind === 'action') return null;

                            if (node.kind === 'view') {
                                return (
                                    <div key={node.id}>
                                        {renderViewField(node, data)}
                                    </div>
                                );
                            }

                            if (node.kind !== 'input') return null;

                            const nodeValue = data[node.id];
                            if (isEmptyValue(nodeValue)) return null;

                            if (node.inputType === 'repeater') {
                                const repeaterItems = Array.isArray(nodeValue)
                                    ? nodeValue
                                    : [];
                                const renderedRepeaterItems =
                                    repeaterItems.length > 0
                                        ? renderRepeaterItems(
                                              node,
                                              repeaterItems,
                                              data,
                                              dataSources
                                          )
                                        : null;
                                if (!renderedRepeaterItems) return null;

                                return (
                                    <div key={node.id} className="space-y-2">
                                        <div className={fieldLabelClassName}>
                                            {node.title}
                                        </div>
                                        {renderedRepeaterItems}
                                    </div>
                                );
                            }

                            return (
                                <div key={node.id} className="space-y-1">
                                    <div className={fieldLabelClassName}>
                                        {node.title}
                                        {readOnlyFieldIds.includes(node.id) && (
                                            <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">
                                                (case context)
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[15px] leading-6">
                                        {formatInputValue(
                                            node,
                                            nodeValue,
                                            dataSources
                                        )}
                                    </div>
                                </div>
                            );
                        })
                        .filter(Boolean);

                    if (!renderedFields.length) {
                        return null;
                    }

                    return (
                        <Card key={section.id}>
                            <CardContent className="space-y-4 p-5">
                                <h4 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                    {section.title}
                                </h4>
                                <div className="space-y-4 border-t pt-4">
                                    {renderedFields}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default CaseDocumentReadView;
